import { getCollection } from '@/utils/helpers/getCollectionByName';
import { BSON } from 'realm-web';
import { TAX_TABLE_COLLECTION } from '@/constants/constants';
import { TaxTableType } from '@/types';

export const getTaxTablesByCompanyId = async (companyId: string): Promise<TaxTableType[] | []> => {
  try {
    const taxTable = getCollection(TAX_TABLE_COLLECTION);
    const taxes = await taxTable?.find({
      companyId: new BSON.ObjectID(companyId),
    });
    if (!taxes) return [];
    return BSON.EJSON.deserialize(taxes) as TaxTableType[];
  } catch (err) {
    console.error('Error fetching taxes', err);
    return [];
  }
};

export const createTaxTable = async (
  taxTable: Partial<TaxTableType>,
): Promise<TaxTableType | undefined> => {
  try {
    const taxTableCollection = getCollection(TAX_TABLE_COLLECTION);
    const bodyWithId = {
      ...taxTable,
      _id: new BSON.ObjectID(),
      companyId: new BSON.ObjectId(taxTable.companyId),
    };
    const newTaxTable = await taxTableCollection?.insertOne(bodyWithId);
    if (newTaxTable) {
      return BSON.EJSON.deserialize(newTaxTable) as TaxTableType;
    } else {
      throw new Error('Could not find new tax table');
    }
  } catch (err) {
    console.error('Error creating tax table', err);
    return undefined;
  }
};

export const updateTaxTableById = async (
  taxTableId: string,
  updateData: Partial<TaxTableType>,
): Promise<TaxTableType | undefined> => {
  try {
    const taxTable = getCollection(TAX_TABLE_COLLECTION);
    const dataWithObjectIds = {
      ...updateData,
      _id: new BSON.ObjectID(updateData._id),
      companyId: new BSON.ObjectID(updateData.companyId),
      tax: updateData?.tax
        ? updateData.tax.map((tax) => ({
            ...tax,
            _id: tax._id ? new BSON.ObjectID(tax._id) : new BSON.ObjectID(),
            rate: tax.rate !== undefined ? new BSON.Double(tax.rate) : undefined,
          }))
        : [],
    };
    const updatedTaxTable = await taxTable?.findOneAndUpdate(
      { _id: new BSON.ObjectID(taxTableId) },
      { $set: dataWithObjectIds },
      { returnNewDocument: true },
    );
    return BSON.EJSON.deserialize(updatedTaxTable) as TaxTableType;
  } catch (err) {
    console.error('Error updating tax table', err);
    return undefined;
  }
};

// Delete a tax table by ID
export const deleteTaxTableById = async (taxTableId: string): Promise<boolean> => {
  try {
    const taxTable = getCollection(TAX_TABLE_COLLECTION);
    const result = await taxTable?.deleteOne({
      _id: new BSON.ObjectID(taxTableId),
    });
    return result?.deletedCount === 1;
  } catch (err) {
    console.error('Error deleting tax table', err);
    return false;
  }
};
